import { FC, useEffect, useMemo, useState } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { CssBaseline, ThemeProvider } from '@mui/material';

import { RevealPage } from '@/pages';
import { store } from '@/store';
import { getTheme, ThemeMode } from '@/theme';

const App: FC = () => {
    // Detect system preference for dark mode
    const [mode, setMode] = useState<ThemeMode>(() => {
        if (typeof window !== 'undefined') {
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            return prefersDark ? 'dark' : 'light';
        }
        return 'light';
    });

    // Listen for system preference changes
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e: MediaQueryListEvent) => {
            setMode(e.matches ? 'dark' : 'light');
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const theme = useMemo(() => getTheme(mode), [mode]);

    return (
        <Provider store={store}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <BrowserRouter>
                    <Routes>
                        {/* Main reveal route - matches /secret/:part1/:part2/:key/:pin? */}
                        <Route path="/secret/:part1/:part2/:key" element={<RevealPage />} />
                        <Route path="/secret/:part1/:part2/:key/:pin" element={<RevealPage />} />

                        {/* Legacy routes without /secret/ prefix */}
                        <Route path="/:part1/:part2/:key" element={<RevealPage />} />
                        <Route path="/:part1/:part2/:key/:pin" element={<RevealPage />} />

                        {/* Fallback for invalid URLs */}
                        <Route path="*" element={<RevealPage />} />
                    </Routes>
                </BrowserRouter>
            </ThemeProvider>
        </Provider>
    );
};

export default App;
